import { useState } from "react";
import axiosInstance from "../../utils/axios-instance";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ParseErrors } from "../../utils/parse-errors";
import { PublicClientApplication } from "@azure/msal-browser";
import BrandedLogo from "../branded-logo";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const publicClientApplication = new PublicClientApplication({
  auth: {
    clientId: process.env.REACT_APP_MICROSOFT_CLIENT_ID,
    redirectUri:
      process.env.NODE_ENV === "production"
        ? window.location.protocol + "//" + window.location.host
        : window.location.protocol +
          "//" +
          window.location.host +
          "/auth-redirect.html",
    authority: `${process.env.REACT_APP_MICROSOFT_AUTHORITY_URL}`,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: true,
  },
});

const MicrosoftLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // --------------------------------- [ MICROSOFT LOGIN ] --------------------------------

  const HandleMicrosoftLogin = async () => {
    setIsLoading(true);
    try {
      await publicClientApplication.initialize();

      const data = await publicClientApplication.loginPopup({
        scopes: ["user.read"],
        prompt: "select_account",
      });

      const graphRes = await fetch(
        "https://graph.microsoft.com/v1.0/me?$select=employeeId,mobilePhone",
        { headers: { Authorization: `Bearer ${data.accessToken}` } },
      );
      const graphData = graphRes.ok ? await graphRes.json() : {};

      await axiosInstance.post("/auth/microsoft/login", {
        idToken: data.idToken,
        employeeId: graphData.employeeId ?? "",
        mobilePhone: graphData.mobilePhone ?? "",
      });

      toast.success("Logged In Successfully");
      setIsLoading(false);
      navigate("/");
    } catch (error) {
      toast.error(ParseErrors(error));
      setIsLoading(false);
      console.log(error);
    }
  };

  return (
    <div className="absolute inset-0 bg-surface rounded-2xl border border-edge shadow-overlay overflow-hidden flex flex-col animate-fade-in">
      {/* Top gradient accent bar */}
      <div className="h-1.5 w-full bg-gradient-to-r from-accent via-blue to-accent-bright shrink-0" />

      <div className="flex flex-col flex-1 items-center justify-between px-[50px] py-[36px]">
        {/* Logo + welcome */}
        <div className="text-center">
          <p className="text-[40px] leading-none mb-3">👋</p>
          <p className="text-label-muted text-[13px] font-medium mb-2">
            Welcome to
          </p>
          <BrandedLogo className="w-[160px] mx-auto object-contain opacity-95" />
        </div>

        {/* Center content */}
        <div className="w-full">
          <div className="text-center mb-6">
            <h2 className="text-[24px] font-heading-font bg-gradient-to-r from-blue to-accent-bright bg-clip-text text-transparent">
              Sign In
            </h2>
            <p className="text-light-gray text-[13px] mt-1">
              Use your Microsoft account to access your workspace
            </p>
          </div>

          {/* Decorative identity icon */}
          <div className="flex justify-center mb-6">
            <div className="w-[64px] h-[64px] rounded-2xl bg-overlay border border-edge flex items-center justify-center shadow-glow-sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 23 23"
              >
                <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
              </svg>
            </div>
          </div>

          {/* Sign-in button */}
          <button
            disabled={isLoading}
            onClick={HandleMicrosoftLogin}
            className="
              w-full flex items-center justify-center gap-3
              px-5 py-[13px] rounded-xl
              border border-edge-strong bg-surface-elevated
              hover:bg-overlay-mid hover:border-indigo-400/40 hover:shadow-glow-sm
              active:scale-[0.98]
              transition-all duration-200
              shadow-card
              disabled:opacity-50 disabled:cursor-not-allowed
              disabled:bg-gradient-to-r disabled:from-accent disabled:to-accent-bright disabled:border-transparent
              group
            "
          >
            {isLoading ? (
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                className="text-white text-[18px]"
              />
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 23 23"
                  className="shrink-0"
                >
                  <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                  <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                  <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                  <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
                </svg>
                <span className="text-[14px] font-medium text-text-primary group-hover:text-blue transition-colors duration-150">
                  Sign in with Microsoft
                </span>
              </>
            )}
          </button>
        </div>

        {/* Footer note */}
        <p className="text-center text-light-gray text-[11px]">
          Secure authentication powered by Microsoft Identity
        </p>
      </div>
    </div>
  );
};

export default MicrosoftLogin;
